import { useCallback, useMemo, useRef, useState } from "react"
import cn from "../../../utils/classes"
import { ClickAwayDiv } from "./ClickAwayDiv"

export interface DropdownBoxProps {
  triggerElement: React.ReactNode
  children: React.ReactNode
  onClick?: () => void
  styleOverrides?: string
}

const DropdownBox: React.FC<DropdownBoxProps> = ({
  triggerElement,
  children,
  onClick,
  styleOverrides,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const triggerElementRef = useRef<HTMLDivElement>(null)
  const clickAwayRef = useRef<HTMLDivElement>(null)
  const className = useMemo(() => {
    const defaultStyles = [
      "absolute",
      "z-10",
      "top-6",
      "right-0",
      "border-1/2",
      "border-solid",
      "border-secondary-grey",
      "rounded-md",
    ]
    return cn(defaultStyles, styleOverrides)
  }, [styleOverrides])

  const handleClick = useCallback(() => {
    setIsOpen((open) => !open)
    onClick && onClick()
  }, [onClick])

  const handleClickEvent = useCallback(
    (event: MouseEvent) => {
      if (
        triggerElementRef.current &&
        clickAwayRef.current &&
        !clickAwayRef.current.contains(event.target as Node) &&
        !triggerElementRef.current.contains(event.target as Node)
      ) {
        handleClick()
      }
    },
    [handleClick, clickAwayRef],
  )

  return (
    <div className="relative">
      <div
        className="cursor-pointer"
        role="button"
        onClick={handleClick}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            handleClick()
          }
        }}
        tabIndex={0}
        ref={triggerElementRef}
      >
        {triggerElement}
      </div>
      {isOpen && (
        <ClickAwayDiv
          containerClassName={className}
          handleClickOutside={handleClickEvent}
          ref={clickAwayRef}
        >
          {children}
        </ClickAwayDiv>
      )}
    </div>
  )
}

export default DropdownBox
